import { useTranslation } from "react-i18next";
import { Label, Toggle } from "@nexus/ui";
import {
  VENDOR_CAPABILITIES,
  resolveVendorCapabilities,
  type VendorCapability,
} from "@nexus/contracts";
import type { EditablePackage } from "../lib/admin-vendor-form.utils";

interface VendorCapabilitiesFieldProps {
  value: VendorCapability[];
  name: string;
  description: string;
  packages: EditablePackage[];
  onChange: (next: VendorCapability[]) => void;
}

export function VendorCapabilitiesField({
  value,
  name,
  description,
  packages,
  onChange,
}: VendorCapabilitiesFieldProps) {
  const { t } = useTranslation();
  const catalogTags = resolveVendorCapabilities({
    name,
    description,
    packages,
    capabilities: value,
  });

  function toggleCapability(capability: VendorCapability) {
    onChange(
      value.includes(capability)
        ? value.filter((item) => item !== capability)
        : [...value, capability],
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label>{t("marketplace.admin.form.capabilities", "Capabilities")}</Label>
      <p className="text-xs text-muted-foreground">
        {t(
          "marketplace.admin.form.capabilitiesHint",
          "Catalog filter tags. Leave empty to infer from name, description, and packages.",
        )}
      </p>
      <div
        role="group"
        aria-label={t("marketplace.admin.form.capabilitiesAria", "Partner capabilities")}
        className="flex flex-wrap gap-1.5"
      >
        {VENDOR_CAPABILITIES.map((capability) => (
          <Toggle
            key={capability}
            variant="outline"
            size="sm"
            pressed={value.includes(capability)}
            onPressedChange={() => toggleCapability(capability)}
            className="h-8 cursor-pointer px-2.5 text-xs"
          >
            {t(`marketplace.vendors.capabilities.${capability}`, capability)}
          </Toggle>
        ))}
      </div>
      {catalogTags.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t("marketplace.admin.form.capabilitiesPreview", {
            tags: catalogTags
              .map((capability) => t(`marketplace.vendors.capabilities.${capability}`, capability))
              .join(", "),
            defaultValue: "Catalog will show: {{tags}}",
          })}
        </p>
      )}
    </div>
  );
}
